import type { ComponentProps, ReactNode } from "react";
import { CopyableText } from "../../primitives/copyable-text";
import { coerceColor, colorValue, namedColor } from "../../lib/color";
import { cn } from "../../lib/utils";
import type { Color } from "../../types";
import type { Size, TextAlign } from "../../generated";

export type TextProps = Omit<ComponentProps<"p">, "children" | "color"> & {
  align?: TextAlign;
  children: ReactNode;
  color?: Color | string | null;
  copyable?: boolean;
  copyLabel?: string;
  copyValue?: string;
  size?: Size;
};

const textAlignments: Record<TextAlign, string> = {
  center: "text-center",
  start: "text-start",
};

const textSizes: Record<Size, string> = {
  xs: "text-xs leading-5",
  sm: "text-sm leading-6",
  md: "text-base leading-7",
  lg: "text-lg leading-8",
  xl: "text-xl leading-8",
  "2xl": "text-2xl leading-9",
  "3xl": "text-3xl leading-10",
  "4xl": "text-4xl leading-none",
};

export function Text({
  align = "start",
  children,
  className,
  color,
  copyable = false,
  copyLabel,
  copyValue,
  size = "md",
  style,
  ...props
}: TextProps) {
  const resolvedColor = colorValue(coerceColor(color) ?? namedColor("muted"));
  const text = (
    <p
      data-slot="text"
      className={cn("m-0", textAlignments[align], textSizes[size], className)}
      style={{ color: resolvedColor, ...style }}
      {...props}
    >
      {children}
    </p>
  );
  const resolvedCopyValue = copyValue ?? (typeof children === "string" ? children : undefined);

  if (!copyable || resolvedCopyValue === undefined) {
    return text;
  }

  return (
    <CopyableText value={resolvedCopyValue} label={copyLabel ?? resolvedCopyValue}>
      {text}
    </CopyableText>
  );
}
